import ExcelJS from 'exceljs';
import type { ReportsData } from '../api/reports.api';

interface ExcelExportOptions {
  data: ReportsData;
  tipoReporte: string;
  startDate: string;
  endDate: string;
  responsable?: string;
  puntoVenta?: string;
}

const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP',
    minimumFractionDigits: 0,
  }).format(value);
};

const formatDateShort = (date: string): string => {
  return new Date(date).toLocaleDateString('es-CO', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
};

// Colores del tema
const COLORS = {
  primary: 'FF14B8A6',      // Teal
  secondary: 'FF3B82F6',    // Blue  
  success: 'FF10B981',      // Green
  warning: 'FFF59E0B',      // Amber
  danger: 'FFF43F5E',       // Rose
  info: 'FF8B5CF6',         // Violet
  light: 'FFF8FAFC',        // Slate 50
  lightGray: 'FFF1F5F9',    // Slate 100
  mediumGray: 'FFE2E8F0',   // Slate 200
  darkGray: 'FF64748B',     // Slate 500
  white: 'FFFFFFFF',
  text: 'FF1E293B',         // Slate 800
};

// Estilos comunes
const headerStyle = {
  font: { bold: true, size: 16, color: { argb: COLORS.white } },
  fill: { type: 'pattern' as const, pattern: 'solid' as const, fgColor: { argb: COLORS.primary } },
  alignment: { horizontal: 'center' as const, vertical: 'middle' as const },
  border: {
    top: { style: 'thin' as const, color: { argb: COLORS.mediumGray } },
    bottom: { style: 'thin' as const, color: { argb: COLORS.mediumGray } },
    left: { style: 'thin' as const, color: { argb: COLORS.mediumGray } },
    right: { style: 'thin' as const, color: { argb: COLORS.mediumGray } },
  }
};

const subHeaderStyle = {
  font: { bold: true, size: 11, color: { argb: COLORS.text } },
  fill: { type: 'pattern' as const, pattern: 'solid' as const, fgColor: { argb: COLORS.lightGray } },
  alignment: { horizontal: 'center' as const, vertical: 'middle' as const },
  border: {
    top: { style: 'thin' as const, color: { argb: COLORS.mediumGray } },
    bottom: { style: 'thin' as const, color: { argb: COLORS.mediumGray } },
    left: { style: 'thin' as const, color: { argb: COLORS.mediumGray } },
    right: { style: 'thin' as const, color: { argb: COLORS.mediumGray } },
  }
};

const totalStyle = {
  font: { bold: true, size: 12, color: { argb: COLORS.white } },
  fill: { type: 'pattern' as const, pattern: 'solid' as const, fgColor: { argb: COLORS.success } },
  alignment: { horizontal: 'left' as const, vertical: 'middle' as const },
  border: {
    top: { style: 'medium' as const, color: { argb: COLORS.success } },
    bottom: { style: 'medium' as const, color: { argb: COLORS.success } },
    left: { style: 'medium' as const, color: { argb: COLORS.success } },
    right: { style: 'medium' as const, color: { argb: COLORS.success } },
  }
};

const infoStyle = {
  font: { size: 10, color: { argb: COLORS.darkGray } },
  alignment: { horizontal: 'left' as const, vertical: 'middle' as const },
};

const cellStyle = {
  border: {
    top: { style: 'thin' as const, color: { argb: COLORS.mediumGray } },
    bottom: { style: 'thin' as const, color: { argb: COLORS.mediumGray } },
    left: { style: 'thin' as const, color: { argb: COLORS.mediumGray } },
    right: { style: 'thin' as const, color: { argb: COLORS.mediumGray } },
  }
};

export const exportReporteIngresosExcel = async (options: ExcelExportOptions) => {
  const { data, startDate, endDate, responsable = 'Admin', puntoVenta = 'General' } = options;
  
  if (!data.ingresos) return;

  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet('Reporte Ingresos');

  // Configurar ancho de columnas
  worksheet.columns = [
    { width: 3 },
    { width: 20 },
    { width: 18 },
    { width: 20 },
    { width: 15 },
    { width: 15 },
  ];

  // TÍTULO PRINCIPAL
  worksheet.mergeCells('B1:F1');
  const titleCell = worksheet.getCell('B1');
  titleCell.value = '💰 REPORTE DE INGRESOS';
  titleCell.style = {
    ...headerStyle,
    font: { ...headerStyle.font, size: 18 }
  };
  worksheet.getRow(1).height = 40;

  worksheet.addRow([]);
  worksheet.addRow([]);

  // INFORMACIÓN DEL REPORTE
  const infoRow1 = worksheet.addRow(['', 'Fecha:', formatDateShort(startDate), 'Responsable:', responsable]);
  const infoRow2 = worksheet.addRow(['', 'Periodo:', `${formatDateShort(startDate)} - ${formatDateShort(endDate)}`, 'Caja / Punto:', puntoVenta]);
  
  [infoRow1, infoRow2].forEach(row => {
    row.getCell(2).style = { ...infoStyle, font: { bold: true, size: 10, color: { argb: COLORS.darkGray } } };
    row.getCell(3).style = infoStyle;
    row.getCell(4).style = { ...infoStyle, font: { bold: true, size: 10, color: { argb: COLORS.darkGray } } };
    row.getCell(5).style = infoStyle;
  });

  worksheet.addRow([]);

  // ENCABEZADOS DE TABLA
  const headerRow = worksheet.addRow(['', 'Método de pago', 'Detalle', 'Valor ($)', '', '']);
  headerRow.height = 30;
  headerRow.eachCell((cell, colNumber) => {
    if (colNumber > 1 && colNumber < 5) {
      cell.style = subHeaderStyle;
    }
  });

  // DATOS - Efectivo
  const efectivoRow = worksheet.addRow(['', '💵', 'Efectivo', formatCurrency(data.ingresos.efectivo), '', '']);
  efectivoRow.getCell(2).style = { ...cellStyle, fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD1FAE5' } }, alignment: { horizontal: 'center', vertical: 'middle' } };
  efectivoRow.getCell(3).style = { ...cellStyle, font: { bold: true } };
  efectivoRow.getCell(4).style = { ...cellStyle, font: { bold: true, color: { argb: COLORS.success } }, numFmt: '"$"#,##0' };

  // DATOS - Tarjeta
  const tarjetaRow = worksheet.addRow(['', '💳', 'Tarjeta', formatCurrency(data.ingresos.tarjeta), '', '']);
  tarjetaRow.getCell(2).style = { ...cellStyle, fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFDBEAFE' } }, alignment: { horizontal: 'center', vertical: 'middle' } };
  tarjetaRow.getCell(3).style = { ...cellStyle, font: { bold: true } };
  tarjetaRow.getCell(4).style = { ...cellStyle, font: { bold: true, color: { argb: COLORS.secondary } }, numFmt: '"$"#,##0' };

  // DATOS - Transferencia
  const transferenciaRow = worksheet.addRow(['', '🏦', 'Transferencia', formatCurrency(data.ingresos.qr), '', '']);
  transferenciaRow.getCell(2).style = { ...cellStyle, fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFEF3C7' } }, alignment: { horizontal: 'center', vertical: 'middle' } };
  transferenciaRow.getCell(3).style = { ...cellStyle, font: { bold: true } };
  transferenciaRow.getCell(4).style = { ...cellStyle, font: { bold: true, color: { argb: COLORS.warning } }, numFmt: '"$"#,##0' };

  worksheet.addRow([]);

  // TOTAL
  const currentRow = worksheet.rowCount + 1;
  worksheet.mergeCells(`B${currentRow}:C${currentRow}`);
  const totalRow = worksheet.addRow(['', 'TOTAL INGRESOS', '', formatCurrency(data.ingresos.total), '', '']);
  totalRow.height = 35;
  totalRow.getCell(2).style = totalStyle;
  totalRow.getCell(4).style = { ...totalStyle, numFmt: '"$"#,##0', alignment: { horizontal: 'right', vertical: 'middle' } };

  worksheet.addRow([]);

  // RESUMEN
  const resumenRow = worksheet.addRow(['', '📊 RESUMEN', '', '', '', '']);
  resumenRow.getCell(2).style = { ...subHeaderStyle, alignment: { horizontal: 'left', vertical: 'middle' } };
  
  const variacionText = `${data.ingresos.variacion >= 0 ? '+' : ''}${data.ingresos.variacion.toFixed(1)}%`;
  const variacionRow = worksheet.addRow(['', 'Variación:', variacionText, '', '', '']);
  variacionRow.getCell(2).style = { ...infoStyle, font: { bold: true, size: 10 } };
  variacionRow.getCell(3).style = {
    ...infoStyle,
    font: { bold: true, size: 11, color: { argb: data.ingresos.variacion >= 0 ? COLORS.success : COLORS.danger } }
  };

  worksheet.addRow([]);

  // FOOTER
  const footerRow = worksheet.addRow(['', `Generado el: ${new Date().toLocaleString('es-CO')}`, '', '', '', '']);
  footerRow.getCell(2).style = { font: { italic: true, size: 9, color: { argb: COLORS.darkGray } } };

  // Guardar archivo
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `Reporte_Ingresos_${startDate.replace(/-/g, '')}_${endDate.replace(/-/g, '')}.xlsx`;
  link.click();
  window.URL.revokeObjectURL(url);
};

export const exportReporteVehiculosExcel = async (options: ExcelExportOptions) => {
  const { data, startDate, endDate, responsable = 'Admin', puntoVenta = 'General' } = options;
  
  if (!data.vehiculos) return;

  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet('Reporte Vehículos');

  worksheet.columns = [
    { width: 3 },
    { width: 22 },
    { width: 15 },
    { width: 15 },
    { width: 20 },
    { width: 12 },
  ];

  // TÍTULO
  worksheet.mergeCells('B1:F1');
  const titleCell = worksheet.getCell('B1');
  titleCell.value = '🚗 REPORTE DE VEHÍCULOS Y MOVIMIENTOS';
  titleCell.style = {
    ...headerStyle,
    font: { ...headerStyle.font, size: 18 },
    fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.secondary } }
  };
  worksheet.getRow(1).height = 40;

  worksheet.addRow([]);
  worksheet.addRow([]);

  // INFO
  const infoRow1 = worksheet.addRow(['', 'Fecha:', formatDateShort(startDate), 'Responsable:', responsable]);
  const infoRow2 = worksheet.addRow(['', 'Periodo:', `${formatDateShort(startDate)} - ${formatDateShort(endDate)}`, 'Caja / Punto:', puntoVenta]);
  
  [infoRow1, infoRow2].forEach(row => {
    row.getCell(2).style = { ...infoStyle, font: { bold: true, size: 10, color: { argb: COLORS.darkGray } } };
    row.getCell(3).style = infoStyle;
    row.getCell(4).style = { ...infoStyle, font: { bold: true, size: 10, color: { argb: COLORS.darkGray } } };
    row.getCell(5).style = infoStyle;
  });

  worksheet.addRow([]);

  // HEADERS
  const headerRow = worksheet.addRow(['', 'Categoría', 'Cantidad', 'Porcentaje', 'Detalle']);
  headerRow.height = 30;
  headerRow.eachCell((cell, colNumber) => {
    if (colNumber > 1 && colNumber < 6) {
      cell.style = { ...subHeaderStyle, fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFDBEAFE' } } };
    }
  });

  const porcentajeCarros = data.vehiculos.total > 0 ? ((data.vehiculos.carros / data.vehiculos.total) * 100).toFixed(1) : '0.0';
  const porcentajeMotos = data.vehiculos.total > 0 ? ((data.vehiculos.motos / data.vehiculos.total) * 100).toFixed(1) : '0.0';

  // DATOS - Carros
  const carrosRow = worksheet.addRow(['', '🚗 Carros', data.vehiculos.carros, `${porcentajeCarros}%`, '']);
  carrosRow.getCell(2).style = { ...cellStyle, font: { bold: true, size: 11 }, fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFDBEAFE' } } };
  carrosRow.getCell(3).style = { ...cellStyle, font: { bold: true, color: { argb: COLORS.secondary } }, alignment: { horizontal: 'center', vertical: 'middle' } };
  carrosRow.getCell(4).style = { ...cellStyle, alignment: { horizontal: 'center', vertical: 'middle' } };

  // DATOS - Motos
  const motosRow = worksheet.addRow(['', '🏍️ Motos', data.vehiculos.motos, `${porcentajeMotos}%`, '']);
  motosRow.getCell(2).style = { ...cellStyle, font: { bold: true, size: 11 }, fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF3E8FF' } } };
  motosRow.getCell(3).style = { ...cellStyle, font: { bold: true, color: { argb: COLORS.info } }, alignment: { horizontal: 'center', vertical: 'middle' } };
  motosRow.getCell(4).style = { ...cellStyle, alignment: { horizontal: 'center', vertical: 'middle' } };

  worksheet.addRow([]);

  // TOTAL
  const currentRow = worksheet.rowCount + 1;
  worksheet.mergeCells(`B${currentRow}:B${currentRow}`);
  const totalRow = worksheet.addRow(['', 'TOTAL VEHÍCULOS', data.vehiculos.total, '100%', '']);
  totalRow.height = 35;
  totalRow.getCell(2).style = totalStyle;
  totalRow.getCell(3).style = { ...totalStyle, alignment: { horizontal: 'center', vertical: 'middle' } };
  totalRow.getCell(4).style = { ...totalStyle, alignment: { horizontal: 'center', vertical: 'middle' } };

  worksheet.addRow([]);

  // ESTADÍSTICAS
  const statsRow = worksheet.addRow(['', '📈 ESTADÍSTICAS', '', '', '']);
  statsRow.getCell(2).style = { ...subHeaderStyle, alignment: { horizontal: 'left' }, fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFEF3C7' } } };
  
  const promedioRow = worksheet.addRow(['', 'Promedio de Horas:', data.vehiculos.promedio.toFixed(2), 'horas por vehículo']);
  promedioRow.getCell(2).style = { ...infoStyle, font: { bold: true, size: 10 } };
  promedioRow.getCell(3).style = { ...infoStyle, font: { bold: true, size: 11, color: { argb: COLORS.warning } } };
  promedioRow.getCell(4).style = infoStyle;

  worksheet.addRow([]);

  const footerRow = worksheet.addRow(['', `Generado el: ${new Date().toLocaleString('es-CO')}`]);
  footerRow.getCell(2).style = { font: { italic: true, size: 9, color: { argb: COLORS.darkGray } } };

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `Reporte_Vehiculos_${startDate.replace(/-/g, '')}_${endDate.replace(/-/g, '')}.xlsx`;
  link.click();
  window.URL.revokeObjectURL(url);
};

export const exportReporteOcupacionExcel = async (options: ExcelExportOptions) => {
  const { data, startDate, endDate, responsable = 'Admin', puntoVenta = 'General' } = options;
  
  if (!data.ocupacion) return;

  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet('Reporte Ocupación');

  worksheet.columns = [
    { width: 3 },
    { width: 25 },
    { width: 12 },
    { width: 15 },
    { width: 20 },
    { width: 12 },
  ];

  // TÍTULO
  worksheet.mergeCells('B1:F1');
  const titleCell = worksheet.getCell('B1');
  titleCell.value = '📊 REPORTE DE OCUPACIÓN DE ESPACIOS';
  titleCell.style = {
    ...headerStyle,
    font: { ...headerStyle.font, size: 18 },
    fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.info } }
  };
  worksheet.getRow(1).height = 40;

  worksheet.addRow([]);
  worksheet.addRow([]);

  const infoRow1 = worksheet.addRow(['', 'Fecha:', formatDateShort(startDate), 'Responsable:', responsable]);
  const infoRow2 = worksheet.addRow(['', 'Periodo:', `${formatDateShort(startDate)} - ${formatDateShort(endDate)}`, 'Caja / Punto:', puntoVenta]);
  
  [infoRow1, infoRow2].forEach(row => {
    row.getCell(2).style = { ...infoStyle, font: { bold: true, size: 10, color: { argb: COLORS.darkGray } } };
    row.getCell(3).style = infoStyle;
    row.getCell(4).style = { ...infoStyle, font: { bold: true, size: 10, color: { argb: COLORS.darkGray } } };
    row.getCell(5).style = infoStyle;
  });

  worksheet.addRow([]);

  // HEADERS
  const headerRow = worksheet.addRow(['', 'Métrica', 'Valor', 'Estado', 'Detalle']);
  headerRow.height = 30;
  headerRow.eachCell((cell, colNumber) => {
    if (colNumber > 1 && colNumber < 6) {
      cell.style = { ...subHeaderStyle, fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF3E8FF' } } };
    }
  });

  const getOcupacionColor = (valor: number) => {
    if (valor > 80) return { bg: 'FFFECACA', text: COLORS.danger, icon: '🔴' };
    if (valor > 50) return { bg: 'FFFEF3C7', text: COLORS.warning, icon: '🟡' };
    return { bg: 'FFD1FAE5', text: COLORS.success, icon: '🟢' };
  };

  const actualColor = getOcupacionColor(data.ocupacion.actual);
  
  // DATOS
  const actualRow = worksheet.addRow(['', '📍 Ocupación Actual', `${data.ocupacion.actual}%`, `${actualColor.icon} ${data.ocupacion.actual > 80 ? 'Alta' : data.ocupacion.actual > 50 ? 'Media' : 'Baja'}`, 'En tiempo real']);
  actualRow.getCell(2).style = { ...cellStyle, font: { bold: true, size: 11 } };
  actualRow.getCell(3).style = { ...cellStyle, font: { bold: true, size: 12, color: { argb: actualColor.text } }, alignment: { horizontal: 'center', vertical: 'middle' }, fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: actualColor.bg } } };
  actualRow.getCell(4).style = { ...cellStyle, alignment: { horizontal: 'center', vertical: 'middle' } };
  actualRow.getCell(5).style = cellStyle;

  const promedioRow = worksheet.addRow(['', '📊 Promedio del Periodo', `${data.ocupacion.promedio}%`, '', 'Promedio general']);
  promedioRow.getCell(2).style = { ...cellStyle, font: { bold: true, size: 11 } };
  promedioRow.getCell(3).style = { ...cellStyle, font: { bold: true, size: 11, color: { argb: COLORS.secondary } }, alignment: { horizontal: 'center', vertical: 'middle' } };
  promedioRow.getCell(5).style = cellStyle;

  const picoRow = worksheet.addRow(['', '⬆️ Pico Máximo', `${data.ocupacion.pico}%`, '', 'Máxima ocupación']);
  picoRow.getCell(2).style = { ...cellStyle, font: { bold: true, size: 11 } };
  picoRow.getCell(3).style = { ...cellStyle, font: { bold: true, size: 11, color: { argb: COLORS.danger } }, alignment: { horizontal: 'center', vertical: 'middle' }, fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFECACA' } } };
  picoRow.getCell(5).style = cellStyle;

  const valleRow = worksheet.addRow(['', '⬇️ Valle Mínimo', `${data.ocupacion.valle}%`, '', 'Mínima ocupación']);
  valleRow.getCell(2).style = { ...cellStyle, font: { bold: true, size: 11 } };
  valleRow.getCell(3).style = { ...cellStyle, font: { bold: true, size: 11, color: { argb: COLORS.success } }, alignment: { horizontal: 'center', vertical: 'middle' }, fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD1FAE5' } } };
  valleRow.getCell(5).style = cellStyle;

  worksheet.addRow([]);

  // ANÁLISIS
  const analisisRow = worksheet.addRow(['', '📈 ANÁLISIS', '', '', '']);
  analisisRow.getCell(2).style = { ...subHeaderStyle, alignment: { horizontal: 'left' }, fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.lightGray } } };
  
  const variacion = data.ocupacion.pico - data.ocupacion.valle;
  const variacionRow = worksheet.addRow(['', 'Variación:', `${variacion}%`, 'puntos de diferencia']);
  variacionRow.getCell(2).style = { ...infoStyle, font: { bold: true, size: 10 } };
  variacionRow.getCell(3).style = { ...infoStyle, font: { bold: true, size: 11, color: { argb: COLORS.info } } };
  variacionRow.getCell(4).style = infoStyle;

  worksheet.addRow([]);

  const footerRow = worksheet.addRow(['', `Generado el: ${new Date().toLocaleString('es-CO')}`]);
  footerRow.getCell(2).style = { font: { italic: true, size: 9, color: { argb: COLORS.darkGray } } };

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `Reporte_Ocupacion_${startDate.replace(/-/g, '')}_${endDate.replace(/-/g, '')}.xlsx`;
  link.click();
  window.URL.revokeObjectURL(url);
};

export const exportReporteOperadoresExcel = async (options: ExcelExportOptions) => {
  const { data, startDate, endDate, responsable = 'Admin', puntoVenta = 'General' } = options;
  
  if (!data.operadores || data.operadores.length === 0) return;

  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet('Reporte Operadores');

  worksheet.columns = [
    { width: 3 },
    { width: 25 },
    { width: 12 },
    { width: 16 },
    { width: 16 },
    { width: 14 },
  ];

  // TÍTULO
  worksheet.mergeCells('B1:F1');
  const titleCell = worksheet.getCell('B1');
  titleCell.value = '👥 REPORTE DE DESEMPEÑO DE OPERADORES';
  titleCell.style = {
    ...headerStyle,
    font: { ...headerStyle.font, size: 18 },
    fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.success } }
  };
  worksheet.getRow(1).height = 40;

  worksheet.addRow([]);
  worksheet.addRow([]);

  const infoRow1 = worksheet.addRow(['', 'Fecha:', formatDateShort(startDate), 'Responsable:', responsable]);
  const infoRow2 = worksheet.addRow(['', 'Periodo:', `${formatDateShort(startDate)} - ${formatDateShort(endDate)}`, 'Caja / Punto:', puntoVenta]);
  
  [infoRow1, infoRow2].forEach(row => {
    row.getCell(2).style = { ...infoStyle, font: { bold: true, size: 10, color: { argb: COLORS.darkGray } } };
    row.getCell(3).style = infoStyle;
    row.getCell(4).style = { ...infoStyle, font: { bold: true, size: 10, color: { argb: COLORS.darkGray } } };
    row.getCell(5).style = infoStyle;
  });

  worksheet.addRow([]);

  // HEADERS
  const headerRow = worksheet.addRow(['', 'Operador', 'Vehículos', 'Ingresos', 'Promedio', 'Eficiencia %']);
  headerRow.height = 30;
  headerRow.eachCell((cell, colNumber) => {
    if (colNumber > 1 && colNumber < 7) {
      cell.style = { ...subHeaderStyle, fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD1FAE5' } } };
    }
  });

  // DATOS de operadores
  const colors = ['FFE0F2FE', 'FFFEF3C7', 'FFF3E8FF', 'FFD1FAE5', 'FFFECACA', 'FFDBEAFE'];
  
  data.operadores.forEach((op, idx) => {
    const row = worksheet.addRow(['', `${idx + 1}. ${op.nombre}`, op.vehiculos, formatCurrency(op.ingresos), formatCurrency(op.promedio), `${op.eficiencia}%`]);
    
    const bgColor = colors[idx % colors.length];
    row.getCell(2).style = { ...cellStyle, font: { bold: true }, fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: bgColor } } };
    row.getCell(3).style = { ...cellStyle, alignment: { horizontal: 'center', vertical: 'middle' }, font: { bold: true } };
    row.getCell(4).style = { ...cellStyle, font: { bold: true, color: { argb: COLORS.success } }, numFmt: '"$"#,##0' };
    row.getCell(5).style = { ...cellStyle, numFmt: '"$"#,##0' };
    row.getCell(6).style = { ...cellStyle, alignment: { horizontal: 'center', vertical: 'middle' }, font: { bold: true, color: { argb: op.eficiencia >= 80 ? COLORS.success : op.eficiencia >= 60 ? COLORS.warning : COLORS.danger } } };
  });

  worksheet.addRow([]);

  // TOTALES
  const totalIngresos = data.operadores.reduce((sum, op) => sum + op.ingresos, 0);
  const totalVehiculos = data.operadores.reduce((sum, op) => sum + op.vehiculos, 0);
  const promedioGeneral = totalVehiculos > 0 ? (totalIngresos / totalVehiculos) : 0;

  const totalesRow = worksheet.addRow(['', '📊 TOTALES Y PROMEDIOS', '', '', '', '']);
  totalesRow.getCell(2).style = { ...subHeaderStyle, alignment: { horizontal: 'left' }, fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.lightGray } } };

  const totalVehRow = worksheet.addRow(['', 'Total Vehículos:', totalVehiculos, 'Total Ingresos:', formatCurrency(totalIngresos)]);
  totalVehRow.getCell(2).style = { ...infoStyle, font: { bold: true, size: 10 } };
  totalVehRow.getCell(3).style = { ...infoStyle, font: { bold: true, size: 11, color: { argb: COLORS.secondary } } };
  totalVehRow.getCell(4).style = { ...infoStyle, font: { bold: true, size: 10 } };
  totalVehRow.getCell(5).style = { ...infoStyle, font: { bold: true, size: 11, color: { argb: COLORS.success } } };

  const promRow = worksheet.addRow(['', 'Promedio General:', formatCurrency(promedioGeneral), 'por vehículo']);
  promRow.getCell(2).style = { ...infoStyle, font: { bold: true, size: 10 } };
  promRow.getCell(3).style = { ...infoStyle, font: { bold: true, size: 11, color: { argb: COLORS.primary } } };
  promRow.getCell(4).style = infoStyle;

  worksheet.addRow([]);

  const footerRow = worksheet.addRow(['', `Generado el: ${new Date().toLocaleString('es-CO')}`]);
  footerRow.getCell(2).style = { font: { italic: true, size: 9, color: { argb: COLORS.darkGray } } };

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `Reporte_Operadores_${startDate.replace(/-/g, '')}_${endDate.replace(/-/g, '')}.xlsx`;
  link.click();
  window.URL.revokeObjectURL(url);
};

export const exportReporteAuditoriaExcel = async (options: ExcelExportOptions) => {
  const { data, startDate, endDate, responsable = 'Admin', puntoVenta = 'General' } = options;
  
  if (!data.auditoria || data.auditoria.length === 0) return;

  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet('Reporte Auditoría');

  worksheet.columns = [
    { width: 3 },
    { width: 20 },
    { width: 18 },
    { width: 25 },
    { width: 18 },
    { width: 12 },
  ];

  // TÍTULO
  worksheet.mergeCells('B1:F1');
  const titleCell = worksheet.getCell('B1');
  titleCell.value = '🔍 REPORTE DE AUDITORÍA Y LOGS';
  titleCell.style = {
    ...headerStyle,
    font: { ...headerStyle.font, size: 18 },
    fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.danger } }
  };
  worksheet.getRow(1).height = 40;

  worksheet.addRow([]);
  worksheet.addRow([]);

  const infoRow1 = worksheet.addRow(['', 'Fecha:', formatDateShort(startDate), 'Responsable:', responsable]);
  const infoRow2 = worksheet.addRow(['', 'Periodo:', `${formatDateShort(startDate)} - ${formatDateShort(endDate)}`, 'Caja / Punto:', puntoVenta]);
  
  [infoRow1, infoRow2].forEach(row => {
    row.getCell(2).style = { ...infoStyle, font: { bold: true, size: 10, color: { argb: COLORS.darkGray } } };
    row.getCell(3).style = infoStyle;
    row.getCell(4).style = { ...infoStyle, font: { bold: true, size: 10, color: { argb: COLORS.darkGray } } };
    row.getCell(5).style = infoStyle;
  });

  worksheet.addRow([]);

  // HEADERS
  const headerRow = worksheet.addRow(['', 'Fecha y Hora', 'Usuario', 'Acción', 'Módulo', 'Estado']);
  headerRow.height = 30;
  headerRow.eachCell((cell, colNumber) => {
    if (colNumber > 1 && colNumber < 7) {
      cell.style = { ...subHeaderStyle, fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFECACA' } } };
    }
  });

  // DATOS - Limitar a 500 registros
  const auditoriaLimitada = data.auditoria.slice(0, 500);
  
  auditoriaLimitada.forEach((log) => {
    const row = worksheet.addRow([
      '',
      new Date(log.fecha).toLocaleString('es-CO'),
      log.usuario,
      log.accion,
      log.modulo,
      log.estado
    ]);
    
    row.getCell(2).style = { ...cellStyle, font: { size: 9 } };
    row.getCell(3).style = { ...cellStyle, font: { bold: true, size: 10 } };
    row.getCell(4).style = cellStyle;
    row.getCell(5).style = { ...cellStyle, fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.lightGray } }, alignment: { horizontal: 'center', vertical: 'middle' } };
    
    const estadoColor = log.estado === 'EXITOSO' || log.estado === 'SUCCESS' ? COLORS.success : 
                       log.estado === 'ERROR' || log.estado === 'FAILED' ? COLORS.danger : 
                       COLORS.warning;
    row.getCell(6).style = { ...cellStyle, font: { bold: true, color: { argb: estadoColor } }, alignment: { horizontal: 'center', vertical: 'middle' } };
  });

  worksheet.addRow([]);

  // RESUMEN
  const resumenRow = worksheet.addRow(['', '📊 RESUMEN', '', '', '', '']);
  resumenRow.getCell(2).style = { ...subHeaderStyle, alignment: { horizontal: 'left' }, fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.lightGray } } };

  const mostradosRow = worksheet.addRow(['', `Total de registros mostrados: ${auditoriaLimitada.length}`, '', '', '', '']);
  mostradosRow.getCell(2).style = { ...infoStyle, font: { bold: true, size: 10 } };

  const totalRow = worksheet.addRow(['', `Total de registros en periodo: ${data.auditoria.length}`, '', '', '', '']);
  totalRow.getCell(2).style = { ...infoStyle, font: { bold: true, size: 10 } };

  worksheet.addRow([]);

  const footerRow = worksheet.addRow(['', `Generado el: ${new Date().toLocaleString('es-CO')}`]);
  footerRow.getCell(2).style = { font: { italic: true, size: 9, color: { argb: COLORS.darkGray } } };

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `Reporte_Auditoria_${startDate.replace(/-/g, '')}_${endDate.replace(/-/g, '')}.xlsx`;
  link.click();
  window.URL.revokeObjectURL(url);
};

// Función principal que maneja todos los tipos de reportes
export const exportReporteExcel = (options: ExcelExportOptions) => {
  const { tipoReporte } = options;

  switch (tipoReporte) {
    case 'ingresos':
      exportReporteIngresosExcel(options);
      break;
    case 'vehiculos':
      exportReporteVehiculosExcel(options);
      break;
    case 'ocupacion':
      exportReporteOcupacionExcel(options);
      break;
    case 'operadores':
      exportReporteOperadoresExcel(options);
      break;
    case 'auditoria':
      exportReporteAuditoriaExcel(options);
      break;
    default:
      // Fallback genérico con ExcelJS
      (async () => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Reporte');
        
        worksheet.columns = [{ width: 3 }, { width: 25 }, { width: 20 }];
        
        worksheet.mergeCells('B1:C1');
        const titleCell = worksheet.getCell('B1');
        titleCell.value = 'REPORTE GENERAL';
        titleCell.style = headerStyle;
        
        worksheet.addRow([]);
        worksheet.addRow(['', 'Tipo de Reporte:', tipoReporte]);
        worksheet.addRow(['', 'Fecha Inicio:', formatDateShort(options.startDate)]);
        worksheet.addRow(['', 'Fecha Fin:', formatDateShort(options.endDate)]);
        worksheet.addRow([]);
        worksheet.addRow(['', `Generado el: ${new Date().toLocaleString('es-CO')}`]);
        
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `Reporte_${tipoReporte}_${options.startDate.replace(/-/g, '')}.xlsx`;
        link.click();
        window.URL.revokeObjectURL(url);
      })();
  }
};
